import {
  checkOrgxAuth,
  peekResolvedOrgxAuth,
  resolveOrgxAuth,
  type OrgxAuthHint,
  type OrgxAuthCheckResult,
} from "../lib/auth.js";
import { checkOpenClawHealth, type OpenClawHealthCheck } from "../lib/openclaw-health.js";
import { checkHostedMcpHealth, type HostedMcpHealthCheck } from "../lib/hosted-mcp-health.js";
import {
  checkHostedMcpToolAccess,
  type HostedMcpToolCheck,
} from "../lib/hosted-mcp-tool-check.js";
import { checkNpmRegistryHealth, type NpmRegistryCheck } from "../lib/npm-registry-health.js";
import {
  checkWorkspaceConnectivity,
  type WorkspaceConnectivityCheck,
} from "../lib/workspace-connectivity.js";
import { getCursorToolingState } from "../lib/plugins.js";
import { readTextIfExists, writeTextFile } from "../lib/fs.js";
import { ORGX_HOSTED_MCP_URL } from "../constants.js";
import {
  inspectDeepseekPlugin,
  inspectDeepseekRuntime,
  installDeepseekPlugin,
  removeDeepseekPlugin,
} from "./deepseek.js";
import {
  inspectClaudeMcpConfig,
  inspectCodexConfigToml,
  inspectCursorMcpConfig,
  inspectVscodeMcpConfig,
  inspectWindsurfMcpConfig,
  inspectZedMcpConfig,
  patchClaudeMcpConfig,
  patchCodexConfigToml,
  patchCursorMcpConfig,
  patchVscodeMcpConfig,
  patchWindsurfMcpConfig,
  patchZedMcpConfig,
  removeClaudeMcpConfig,
  removeCodexConfigToml,
  removeCursorMcpConfig,
  removeVscodeMcpConfig,
  removeWindsurfMcpConfig,
  removeZedMcpConfig,
} from "./mcp-config.js";
import {
  buildLocalMcpUrl,
  inspectOpenClawConfig,
  patchOpenClawConfig,
  removeOpenClawConfig,
} from "./openclaw-config.js";
import {
  AUTOMATED_SURFACE_NAMES,
  MCP_SURFACE_NAMES,
  SURFACE_NAMES,
  type SurfaceMode,
  type SurfaceName,
} from "./names.js";
import {
  detectSurface,
  getSurfaceLocator,
  type SurfaceDetection,
} from "./detection.js";

export { SURFACE_NAMES } from "./names.js";
export type { SurfaceName } from "./names.js";

export interface SurfaceStatus {
  name: SurfaceName;
  mode: SurfaceMode;
  detected: boolean;
  configured: boolean;
  path?: string;
  details: string[];
  summary: string;
  /**
   * When `name` is "cursor", distinguishes MCP lines in `~/.cursor/mcp.json` from the
   * managed companion bundle under `~/.cursor/plugins/local/cursor-plugin`.
   */
  cursorIntegration?: {
    mcpLocalOpenclaw: boolean;
    mcpHostedOrgx: boolean;
    pluginBundleInstalled: boolean;
  };
}

export interface SurfaceMutationResult {
  name: SurfaceName;
  changed: boolean;
  path?: string;
  message: string;
}

type SurfaceSelection<T extends string> = T | "all" | readonly (T | "all")[];

interface OpenClawDependencyState {
  detected: boolean;
  configured: boolean;
  path?: string;
  url?: string;
}

const AUTH_SETUP_HINT = "orgx-wizard auth login";
const AUTH_SET_KEY_HINT = "orgx-wizard auth set-key";
const DOCTOR_HINT = "orgx-wizard doctor";

function getSurfaceMode(name: SurfaceName): SurfaceMode {
  return AUTOMATED_SURFACE_NAMES.includes(name as (typeof AUTOMATED_SURFACE_NAMES)[number])
    ? "automated"
    : "manual";
}

function getSurfacePath(name: SurfaceName): string | undefined {
  const detection = detectSurface(name);
  const locator = getSurfaceLocator(name);
  const existingPath = detection.existingPath;

  if (existingPath && locator.configPaths.includes(existingPath)) {
    return existingPath;
  }

  return locator.configPaths[0] ?? detection.preferredPath ?? existingPath;
}

function getOpenClawDependencyState(): OpenClawDependencyState {
  const detection = detectSurface("openclaw");
  const path = detection.existingPath ?? detection.preferredPath;
  const raw = path ? readTextIfExists(path) : null;
  const inspection = inspectOpenClawConfig(raw);

  const result: OpenClawDependencyState = {
    detected: detection.detected,
    configured: inspection.configured,
  };

  if (path) {
    result.path = path;
  }

  if (detection.detected) {
    result.url = buildLocalMcpUrl(raw);
  }

  return result;
}

function automatedSurfaceStatus(
  name: Extract<
    SurfaceName,
    "claude" | "cursor" | "codex" | "deepseek" | "openclaw" | "vscode" | "windsurf" | "zed"
  >,
): SurfaceStatus {
  const detection = detectSurface(name);
  const path = detection.existingPath ?? detection.preferredPath;
  const openclaw = getOpenClawDependencyState();

  if (name === "deepseek") {
    const runtime = inspectDeepseekRuntime();
    const plugin = path
      ? inspectDeepseekPlugin(path)
      : { installed: false, details: ["DeepSeek profile path is unavailable"] };
    return {
      name,
      mode: "automated",
      detected: detection.detected || runtime.installed,
      configured: runtime.supported && plugin.installed,
      ...(path ? { path } : {}),
      details: [...runtime.details, ...plugin.details, ...detection.evidence],
      summary: !runtime.installed
        ? "DeepSeek Harness was not detected."
        : !runtime.supported
          ? "DeepSeek Harness is installed, but the version is not supported by the OrgX plugin."
          : plugin.installed
            ? "OrgX is installed in the DeepSeek Harness headless profile."
            : "DeepSeek Harness is ready for the OrgX plugin.",
    };
  }

  if (name === "claude") {
    const inspection = inspectClaudeMcpConfig(path ? readTextIfExists(path) : null);
    const configured = inspection.hostedConfigured === true &&
      (openclaw.detected ? inspection.localConfigured === true : true);
    return {
      name,
      mode: "automated",
      detected: detection.detected,
      configured,
      ...(path ? { path } : {}),
      details: [
        ...inspection.details,
        ...(openclaw.detected
          ? [`OpenClaw bridge: ${openclaw.configured ? "configured" : "detected but not configured"}`]
          : ["OpenClaw bridge: not detected; hosted OrgX is the only available route."]),
        ...detection.evidence,
      ],
      summary: configured
        ? openclaw.detected
          ? "OrgX is connected in Claude with the local OpenClaw bridge."
          : "OrgX cloud MCP is connected in Claude."
        : detection.detected
          ? openclaw.detected
            ? `Claude is missing either hosted OrgX or the local OpenClaw bridge (${openclaw.url ?? "unknown"}).`
            : "Claude is missing the hosted OrgX MCP entry."
          : "Claude config directory was not detected on disk.",
    };
  }

  if (name === "cursor") {
    const inspection = inspectCursorMcpConfig(path ? readTextIfExists(path) : null);
    const tooling = getCursorToolingState();
    const configured = openclaw.detected && inspection.localConfigured === true;
    const mcpWired = tooling.mcpLocalOpenclaw || tooling.mcpHostedOrgx;
    const summary = (() => {
      if (!detection.detected) {
        return "Cursor config directory was not detected on disk.";
      }
      if (configured) {
        return tooling.pluginBundleInstalled
          ? "OrgX is connected in Cursor through the local OpenClaw bridge; companion plugin bundle is installed."
          : "OrgX is connected in Cursor through the local OpenClaw bridge; companion plugin bundle (skills, hooks, rules) is not installed.";
      }
      if (mcpWired && !tooling.pluginBundleInstalled) {
        if (tooling.mcpHostedOrgx && !openclaw.detected) {
          return "OrgX hosted MCP is present in Cursor mcp.json; install OpenClaw for the local bridge, or add the companion plugin bundle for the full pack.";
        }
        return "OrgX MCP is present in Cursor mcp.json, but the companion plugin bundle (skills, hooks, rules) is not installed.";
      }
      if (detection.detected) {
        return openclaw.detected
          ? `Cursor is missing the local OrgX OpenClaw bridge (${openclaw.url ?? "unknown"}).`
          : "Cursor is ready to wire, but OpenClaw must be installed first.";
      }
      return "Cursor config directory was not detected on disk.";
    })();
    return {
      name,
      mode: "automated",
      detected: detection.detected,
      configured,
      ...(path ? { path } : {}),
      cursorIntegration: tooling,
      details: [
        `companion plugin bundle: ${tooling.pluginBundleInstalled ? "installed" : "not installed"}`,
        ...inspection.details,
        ...(openclaw.detected
          ? [`OpenClaw bridge: ${openclaw.configured ? "configured" : "detected but not configured"}`]
          : ["OpenClaw bridge: not detected; Cursor cannot use the local OrgX bridge yet."]),
        ...detection.evidence,
      ],
      summary,
    };
  }

  if (name === "codex") {
    const inspection = inspectCodexConfigToml(path ? readTextIfExists(path) : null);
    const configured = inspection.hostedConfigured === true &&
      (openclaw.detected ? inspection.localConfigured === true : true);
    return {
      name,
      mode: "automated",
      detected: detection.detected,
      configured,
      ...(path ? { path } : {}),
      details: [
        ...inspection.details,
        ...(openclaw.detected
          ? [`OpenClaw bridge: ${openclaw.configured ? "configured" : "detected but not configured"}`]
          : ["OpenClaw bridge: not detected; hosted OrgX is the only available route."]),
        ...detection.evidence,
      ],
      summary: configured
        ? openclaw.detected
          ? "OrgX is connected in Codex with the local OpenClaw bridge."
          : "OrgX cloud MCP is connected in Codex."
        : detection.detected
          ? openclaw.detected
            ? `Codex is missing either hosted OrgX or the local OpenClaw bridge (${openclaw.url ?? "unknown"}).`
            : "Codex is missing the hosted OrgX MCP entry."
          : "Codex config directory was not detected on disk.",
    };
  }

  if (name === "vscode") {
    const inspection = inspectVscodeMcpConfig(path ? readTextIfExists(path) : null);
    return {
      name,
      mode: "automated",
      detected: detection.detected,
      configured: inspection.hostedConfigured === true,
      ...(path ? { path } : {}),
      details: [...inspection.details, ...detection.evidence],
      summary: inspection.hostedConfigured
        ? "OrgX cloud MCP is connected in VS Code."
        : detection.detected
          ? "VS Code is missing the hosted OrgX MCP entry."
          : "VS Code config directory was not detected on disk.",
    };
  }

  if (name === "windsurf") {
    const inspection = inspectWindsurfMcpConfig(path ? readTextIfExists(path) : null);
    return {
      name,
      mode: "automated",
      detected: detection.detected,
      configured: inspection.hostedConfigured === true,
      ...(path ? { path } : {}),
      details: [...inspection.details, ...detection.evidence],
      summary: inspection.hostedConfigured
        ? "OrgX cloud MCP is connected in Windsurf."
        : detection.detected
          ? "Windsurf is missing the hosted OrgX MCP entry."
          : "Windsurf config directory was not detected on disk.",
    };
  }

  if (name === "zed") {
    const inspection = inspectZedMcpConfig(path ? readTextIfExists(path) : null);
    return {
      name,
      mode: "automated",
      detected: detection.detected,
      configured: inspection.hostedConfigured === true,
      ...(path ? { path } : {}),
      details: [...inspection.details, ...detection.evidence],
      summary: inspection.hostedConfigured
        ? "OrgX cloud MCP is connected in Zed."
        : detection.detected
          ? "Zed is missing the hosted OrgX MCP entry."
          : "Zed config directory was not detected on disk.",
    };
  }

  const inspection = inspectOpenClawConfig(path ? readTextIfExists(path) : null);
  return {
    name,
    mode: "automated",
    detected: detection.detected,
    configured: inspection.configured,
    ...(path ? { path } : {}),
    details: [...inspection.details, ...detection.evidence],
    summary: inspection.configured
      ? `OpenClaw plugin entry ${inspection.pluginKey ?? "missing"} is configured.`
      : inspection.installed
        ? "OpenClaw is installed but OrgX defaults have not been written."
        : detection.detected
          ? "OpenClaw support files exist but no OrgX plugin entry was found."
          : "OpenClaw is not installed or no support files were found.",
  };
}

function formatAuthHintSource(authHint: OrgxAuthHint): string {
  switch (authHint.source) {
    case "environment":
      return "env ORGX_API_KEY";
    case "wizard-store":
      return "wizard auth store";
    case "openclaw-config-file":
      return "openclaw config";
  }
}

export function buildChatgptManualStatus(
  detection: SurfaceDetection,
  authHint: OrgxAuthHint | null,
): SurfaceStatus {
  const path = detection.existingPath ?? detection.preferredPath;

  const details = [...detection.evidence];
  if (authHint) {
    details.push(
      `OrgX auth ready via ${formatAuthHintSource(authHint)} (${authHint.keyPrefix})`,
    );
    details.push(`Hosted connector URL: ${ORGX_HOSTED_MCP_URL}`);
    details.push(
      "Manual step: in ChatGPT desktop developer mode, add a custom connector pointing at the hosted OrgX MCP URL.",
    );
    details.push(
      "ChatGPT completes OAuth during connector setup; the wizard cannot preflight hosted MCP tools with an oxk_ API key.",
    );
    details.push(
      "After connecting ChatGPT, run `orgx-wizard doctor` to verify hosted MCP health from this machine.",
    );
  } else {
    details.push(
      "OrgX auth is not configured yet. Run `orgx-wizard auth login` or `orgx-wizard auth set-key` first.",
    );
    details.push(
      "After auth is configured, add a custom ChatGPT developer connector pointing at the hosted OrgX MCP URL.",
    );
  }

  let summary: string;
  if (!authHint) {
    summary = detection.detected
      ? "ChatGPT is installed, but OrgX auth is not configured yet."
      : "ChatGPT support files were not found; configure OrgX auth first, then add the hosted connector manually.";
  } else {
    summary = detection.detected
      ? "ChatGPT is installed; finish the manual developer connector setup."
      : "OrgX auth is ready, but ChatGPT desktop support files were not found on disk.";
  }

  return {
    name: "chatgpt",
    mode: "manual",
    detected: detection.detected,
    configured: false,
    ...(path ? { path } : {}),
    details,
    summary,
  };
}

export function buildChatgptAddResult(
  path: string | undefined,
  authHint: OrgxAuthHint | null,
  toolCheck?: HostedMcpToolCheck,
): SurfaceMutationResult {
  if (!authHint) {
    return {
      name: "chatgpt",
      changed: false,
      ...(path ? { path } : {}),
      message:
        `Run \`${AUTH_SETUP_HINT}\` or \`${AUTH_SET_KEY_HINT}\` first, then add ${ORGX_HOSTED_MCP_URL} as a custom connector in ChatGPT developer mode.`,
    };
  }

  if (toolCheck && !toolCheck.ok && !toolCheck.skipped) {
    const failure = toolCheck?.error ?? "hosted MCP verification did not complete.";
    return {
      name: "chatgpt",
      changed: false,
      ...(path ? { path } : {}),
      message:
        `OrgX auth is configured via ${formatAuthHintSource(authHint)}, but hosted MCP verification failed (${failure}). Fix that first, then add ${ORGX_HOSTED_MCP_URL} in ChatGPT developer mode.`,
    };
  }

  return {
    name: "chatgpt",
    changed: false,
    ...(path ? { path } : {}),
    message:
      `OrgX auth is ready via ${formatAuthHintSource(authHint)}. Add ${ORGX_HOSTED_MCP_URL} as a custom connector in ChatGPT developer mode; ChatGPT will complete the OAuth flow there. Then run \`${DOCTOR_HINT}\` to verify hosted MCP health from this machine.`,
  };
}

export function buildChatgptRemoveResult(path: string | undefined): SurfaceMutationResult {
  return {
    name: "chatgpt",
    changed: false,
    ...(path ? { path } : {}),
    message:
      "Remove the custom OrgX connector from ChatGPT developer mode; the wizard does not manage ChatGPT config files directly.",
  };
}

function manualSurfaceStatus(name: Extract<SurfaceName, "chatgpt">): SurfaceStatus {
  const detection = detectSurface(name);

  switch (name) {
    case "chatgpt":
      return buildChatgptManualStatus(detection, peekResolvedOrgxAuth());
  }
}

function getSurfaceStatus(name: SurfaceName): SurfaceStatus {
  if (AUTOMATED_SURFACE_NAMES.includes(name as (typeof AUTOMATED_SURFACE_NAMES)[number])) {
    return automatedSurfaceStatus(
      name as Extract<
        SurfaceName,
        "claude" | "cursor" | "codex" | "deepseek" | "openclaw" | "vscode" | "windsurf" | "zed"
      >,
    );
  }

  return manualSurfaceStatus(name as Extract<SurfaceName, "chatgpt">);
}

export function listSurfaceStatuses(): SurfaceStatus[] {
  return SURFACE_NAMES.map(getSurfaceStatus);
}

function resolveTargets<T extends string>(
  selection: SurfaceSelection<T>,
  allTargets: readonly T[],
): T[] {
  const values = Array.isArray(selection) ? selection : [selection];

  if (values.includes("all")) {
    return [...allTargets];
  }

  return [...new Set(values)] as T[];
}

export function resolveSurfaceSelection(
  selection: SurfaceSelection<SurfaceName>,
): SurfaceName[] {
  return resolveTargets(selection, SURFACE_NAMES);
}

export function resolveMcpSurfaceSelection(
  selection: SurfaceSelection<(typeof MCP_SURFACE_NAMES)[number]>,
): Array<(typeof MCP_SURFACE_NAMES)[number]> {
  return resolveTargets(selection, MCP_SURFACE_NAMES);
}

async function addAutomatedSurface(name: SurfaceName): Promise<SurfaceMutationResult> {
  const path = getSurfacePath(name);
  const openclaw = getOpenClawDependencyState();

  if (!path) {
    return {
      name,
      changed: false,
      message: `${name} has no writable config path on this platform.`,
    };
  }

  switch (name) {
    case "claude": {
      const previous = readTextIfExists(path);
      const next = patchClaudeMcpConfig(previous, openclaw.url);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: openclaw.detected
          ? "OrgX is connected in Claude with the local OpenClaw bridge."
          : "OrgX cloud MCP is connected in Claude.",
      };
    }
    case "cursor": {
      if (!openclaw.detected || !openclaw.url) {
        return {
          name,
          changed: false,
          path,
          message: "Cursor needs OpenClaw installed before OrgX can be connected locally.",
        };
      }
      const previous = readTextIfExists(path);
      const next = patchCursorMcpConfig(previous, openclaw.url);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OrgX is connected in Cursor through the local OpenClaw bridge.",
      };
    }
    case "codex": {
      const previous = readTextIfExists(path);
      const next = patchCodexConfigToml(previous, openclaw.url);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: openclaw.detected
          ? "OrgX is connected in Codex with the local OpenClaw bridge."
          : "OrgX cloud MCP is connected in Codex.",
      };
    }
    case "deepseek": {
      const result = installDeepseekPlugin(path);
      return {
        name,
        changed: result.changed,
        path,
        message: result.message,
      };
    }
    case "openclaw": {
      const previous = readTextIfExists(path);
      const auth = await resolveOrgxAuth();
      const next = patchOpenClawConfig(previous, auth ?? {});
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: auth
          ? "OpenClaw is configured as the local OrgX bridge with the resolved OrgX auth."
          : "OpenClaw is configured as the local OrgX bridge.",
      };
    }
    case "vscode": {
      const previous = readTextIfExists(path);
      if (inspectVscodeMcpConfig(previous).hostedConfigured) {
        return { name, changed: false, path, message: "OrgX cloud MCP is connected in VS Code." };
      }
      const next = patchVscodeMcpConfig(previous);
      writeTextFile(path, next);
      return { name, changed: true, path, message: "OrgX cloud MCP is connected in VS Code." };
    }
    case "windsurf": {
      const previous = readTextIfExists(path);
      if (inspectWindsurfMcpConfig(previous).hostedConfigured) {
        return { name, changed: false, path, message: "OrgX cloud MCP is connected in Windsurf." };
      }
      const next = patchWindsurfMcpConfig(previous);
      writeTextFile(path, next);
      return { name, changed: true, path, message: "OrgX cloud MCP is connected in Windsurf." };
    }
    case "zed": {
      const previous = readTextIfExists(path);
      if (inspectZedMcpConfig(previous).hostedConfigured) {
        return { name, changed: false, path, message: "OrgX cloud MCP is connected in Zed." };
      }
      const next = patchZedMcpConfig(previous);
      writeTextFile(path, next);
      return { name, changed: true, path, message: "OrgX cloud MCP is connected in Zed." };
    }
    case "chatgpt": {
      const authHint = peekResolvedOrgxAuth();
      if (!authHint) {
        return buildChatgptAddResult(path, null);
      }
      return buildChatgptAddResult(path, authHint);
    }
    default:
      return {
        name,
        changed: false,
        message: `${name} is not supported by the wizard.`,
      };
  }
}

function removeAutomatedSurface(name: SurfaceName): SurfaceMutationResult {
  const path = getSurfacePath(name);

  if (!path) {
    return {
      name,
      changed: false,
      message: `${name} has no writable config path on this platform.`,
    };
  }

  switch (name) {
    case "claude": {
      const previous = readTextIfExists(path);
      const next = removeClaudeMcpConfig(previous);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OrgX connection was removed from Claude.",
      };
    }
    case "cursor": {
      const previous = readTextIfExists(path);
      const next = removeCursorMcpConfig(previous);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OrgX connection was removed from Cursor.",
      };
    }
    case "codex": {
      const previous = readTextIfExists(path);
      const next = removeCodexConfigToml(previous);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OrgX connection was removed from Codex.",
      };
    }
    case "deepseek": {
      const result = removeDeepseekPlugin(path);
      return {
        name,
        changed: result.changed,
        path,
        message: result.message,
      };
    }
    case "openclaw": {
      const previous = readTextIfExists(path);
      const next = removeOpenClawConfig(previous);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OpenClaw OrgX defaults were removed.",
      };
    }
    case "vscode": {
      const previous = readTextIfExists(path);
      const next = removeVscodeMcpConfig(previous);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OrgX connection was removed from VS Code.",
      };
    }
    case "windsurf": {
      const previous = readTextIfExists(path);
      const next = removeWindsurfMcpConfig(previous);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OrgX connection was removed from Windsurf.",
      };
    }
    case "zed": {
      const previous = readTextIfExists(path);
      const next = removeZedMcpConfig(previous);
      writeTextFile(path, next);
      return {
        name,
        changed: previous !== next,
        path,
        message: "OrgX connection was removed from Zed.",
      };
    }
    case "chatgpt":
      return buildChatgptRemoveResult(path);
    default:
      return {
        name,
        changed: false,
        message: `${name} is not supported by the wizard.`,
      };
  }
}

export async function addSurface(
  selection: SurfaceSelection<SurfaceName>,
): Promise<SurfaceMutationResult[]> {
  const targets = resolveSurfaceSelection(selection);
  const results: SurfaceMutationResult[] = [];

  for (const surface of targets) {
    results.push(await addAutomatedSurface(surface));
  }

  return results;
}

export function removeSurface(selection: SurfaceSelection<SurfaceName>): SurfaceMutationResult[] {
  const targets = resolveSurfaceSelection(selection);
  return targets.map((surface) => removeAutomatedSurface(surface));
}

export async function addMcpSurface(
  selection: SurfaceSelection<(typeof MCP_SURFACE_NAMES)[number]>,
): Promise<SurfaceMutationResult[]> {
  const targets = resolveMcpSurfaceSelection(selection);
  const results: SurfaceMutationResult[] = [];

  for (const surface of targets) {
    results.push(await addAutomatedSurface(surface));
  }

  return results;
}

export function removeMcpSurface(
  selection: SurfaceSelection<(typeof MCP_SURFACE_NAMES)[number]>,
): SurfaceMutationResult[] {
  const targets = resolveMcpSurfaceSelection(selection);
  return targets.map((surface) => removeAutomatedSurface(surface));
}

export async function setupDetectedSurfaces(): Promise<SurfaceMutationResult[]> {
  const targets = AUTOMATED_SURFACE_NAMES.filter((name) => getSurfaceStatus(name).detected);

  targets.sort((left, right) => {
    if (left === "openclaw") return -1;
    if (right === "openclaw") return 1;
    return 0;
  });

  const results: SurfaceMutationResult[] = [];

  for (const name of targets) {
    results.push(await addAutomatedSurface(name));
  }

  return results;
}

export interface DoctorReport {
  surfaces: SurfaceStatus[];
  auth: OrgxAuthCheckResult;
  hostedMcp: HostedMcpHealthCheck;
  hostedMcpTool: HostedMcpToolCheck;
  npmRegistry: NpmRegistryCheck;
  workspace: WorkspaceConnectivityCheck;
  openclaw: OpenClawHealthCheck;
}

export interface DoctorAssessmentIssue {
  level: "error" | "warning";
  title: string;
  suggestion: string;
}

export interface DoctorAssessment {
  ok: boolean;
  issues: DoctorAssessmentIssue[];
}

function skippedOpenClawHealth(): OpenClawHealthCheck {
  return {
    ok: false,
    skipped: true,
    method: "none",
    error: "OpenClaw support files were not detected; skipping gateway health check.",
    details: [],
  };
}

export interface RunDoctorOptions {
  /**
   * Pre-resolved workspace connectivity result. When provided, the doctor skips
   * the live workspace lookup — useful right after `setup` has already
   * successfully resolved the workspace, so we don't re-pay a cold-start round
   * trip just to render the final report.
   */
  cachedWorkspace?: WorkspaceConnectivityCheck;
}

export async function runDoctor(options: RunDoctorOptions = {}): Promise<DoctorReport> {
  const surfaces = listSurfaceStatuses();
  const openclawSurface = surfaces.find((surface) => surface.name === "openclaw");

  // Network checks are independent — run them in parallel so the final
  // verification finishes in roughly the slowest single check rather than
  // the sum of all of them.
  const [auth, hostedMcp, hostedMcpTool, npmRegistry, workspace, openclaw] = await Promise.all([
    checkOrgxAuth(),
    checkHostedMcpHealth(),
    checkHostedMcpToolAccess(),
    checkNpmRegistryHealth(),
    options.cachedWorkspace ? Promise.resolve(options.cachedWorkspace) : checkWorkspaceConnectivity(),
    openclawSurface?.detected
      ? checkOpenClawHealth(openclawSurface.path)
      : Promise.resolve(skippedOpenClawHealth()),
  ]);

  return { surfaces, auth, hostedMcp, hostedMcpTool, npmRegistry, workspace, openclaw };
}

export function assessDoctorReport(report: DoctorReport): DoctorAssessment {
  const issues: DoctorAssessmentIssue[] = [];

  if (!report.hostedMcp.ok) {
    issues.push({
      level: "error",
      title: "Hosted OrgX MCP is unreachable.",
      suggestion:
        "Retry later or verify https://mcp.useorgx.com/health. Hosted-only clients will stay broken until that endpoint recovers.",
    });
  }

  if (
    report.hostedMcp.ok &&
    report.hostedMcpTool.configured &&
    !report.hostedMcpTool.ok &&
    !report.hostedMcpTool.skipped
  ) {
    issues.push({
      level: "error",
      title: `Hosted OrgX MCP tool verification failed (${report.hostedMcpTool.toolName}).`,
      suggestion:
        "Re-run `wizard auth login` to refresh the per-user key, then retry `wizard doctor` to confirm the hosted MCP can initialize and execute tools.",
    });
  }

  if (report.auth.configured && !report.auth.ok && !report.auth.skipped) {
    issues.push({
      level: "error",
      title: "OrgX user auth could not be verified.",
      suggestion:
        "Run `wizard auth set-key <oxk_...>` again or correct the resolved ORGX_API_KEY / base URL before retrying.",
    });
  }

  if (report.workspace.configured && !report.workspace.ok) {
    issues.push({
      level: "error",
      title: "Current workspace lookup failed.",
      suggestion:
        "Verify the resolved OrgX base URL and that the authenticated user still has access to a workspace.",
    });
  }

  if (!report.npmRegistry.reachable) {
    issues.push({
      level: "warning",
      title: "npm registry is unreachable.",
      suggestion:
        "Check outbound network access to https://registry.npmjs.org before relying on npm-based installation flows.",
    });
  } else if (!report.npmRegistry.published) {
    issues.push({
      level: "warning",
      title: `${report.npmRegistry.packageName} is not published on npm yet.`,
      suggestion:
        "This is expected before launch. Publish the package in the release workstream before asking users to install from npm.",
    });
  }

  if (!report.openclaw.skipped && !report.openclaw.ok) {
    issues.push({
      level: "warning",
      title: "OpenClaw is installed but its local gateway health check failed.",
      suggestion:
        "Run `openclaw doctor` or `openclaw doctor --repair` before relying on local bridge clients like Cursor.",
    });
  }

  const cursor = report.surfaces.find((surface) => surface.name === "cursor");
  const ci = cursor?.cursorIntegration;
  if (cursor?.detected && ci && (ci.mcpLocalOpenclaw || ci.mcpHostedOrgx) && !ci.pluginBundleInstalled) {
    issues.push({
      level: "warning",
      title: "Cursor has OrgX MCP wiring but not the full companion plugin bundle.",
      suggestion:
        "Run `orgx-wizard plugins add cursor` to install the managed pack (skills, rules, hooks, commands), then reload Cursor. Skip this if you intentionally use MCP-only mode.",
    });
  }

  return {
    ok: !issues.some((issue) => issue.level === "error"),
    issues,
  };
}
